import { Config } from 'config';

import { forEach, forOwn, map, keys, includes,
         isFunction, isPlainObject, isSymbol,
         toArray as _toArray,
         clone as _clone,
         cloneDeep as _cloneDeep } from 'lodash';

function error(message) {
  console.log('[Cx] '+message);
  if( Config.debug ) {
    throw(message);
  }
}

let Type = {};

/**
* Simple JavaScript Inheritance
* By John Resig http://ejohn.org/
* MIT Licensed.
* Inspired by base2 and Prototype.
*
* Event support added by Todd Murdock
* JQuery-like Properties support added by Matias Capeletto
*/

// We set this to callParent to avoid modifying objects structure
// Originally it was deleted
function noop() {}


export const isNativeArray = Array.isArray ;

export const isArray = function(v) {
  return (v && v.__CxArray) || isNativeArray(v);
};

export const toArray = _toArray ;

export function clone(v) {
  return v && v.clone ? v.clone() : _clone(v);
}

export function cloneDeep(v) {
  if( v ) {
    if( v.cloneDeep ) {
      return v.cloneDeep();
    }
    if( v.clone ) {
      return v.clone();
    }
    if( isNativeArray(v) ) {
      const r = [];
      for(let k=0,kEnd=v.length;k<kEnd;++k) {
        r[k] = cloneDeep(v[k]);
      }
      return r;
    }
  }
  return _cloneDeep(v);
}


export function concat(array1, array2) {
  return (array1.__CxArray ? array1.toArray() : array1).concat( array2 && array2.__CxArray ? array2.toArray() : array2 );
}

export function flatten(array,r) {
  if( r === undefined ) {
    r = [];
  }
  for( let k = 0, kEnd = array.length; k < kEnd; ++k ) {
    const ak = array[k];
    if( isArray(ak) ) {
      flatten( ak, r );
    }
    else {
      r.push( ak );
    }
  }
  return r;
}

export function flatMap( a, f ) {
  return flatten(map(a,f));
}

let initializing = false;

const fnTest = /xyz/.test(function() { xyz; }) ? /\bcallParent\b/ : /.*/;

// [TODO] enable doc.clear() idiom <---

const definePropertyImp = function(object,name,member,get,set,onChange) {
  Object.defineProperty(object,name,{
    get: get || function() { return this[member]; },
    set: onChange ? ( set ? function(v) { set.call(this,v); onChange.call(this); }
                          : function(v) { this[member] = v; onChange.call(this); } )
                  : ( set || function(v) { this[member] = v; } )
  });
};

const defineArrayPropertyImp = function(object,name,member) {
  Object.defineProperty(object,name,{
    get() { return this[member]; },
    set( v ) { this[member].set(v); }
  });
};


const builtIn = [ 'number', 'integer', 'angle', 'string', 'boolean', 'magnitude', 'function', 'char' ];
const primitives = [ 'Point', 'Matrix', 'Matrix22', 'Bounds', 'Frame', 'Segment', 'Quadratic', 'Cubic' ];

const _makeConstructFunction = function(typeName,owned) {

  // Explicit not owned objects
  if( owned === false ) {
    return function(v) {
      return v;
    };
  }

  // Built in primitives
  if( includes(builtIn,typeName) ) {
    return function(v) {
      return v;
    };
  }

  // Grafika Primitives
  if( includes(primitives,typeName) ) {
    return function(v) {
      return v;
    };
  }

  const KnowConstructor = Type[typeName];
  if( KnowConstructor && 'construct' in KnowConstructor ) {
    // base.log(`${typeName} special construct`);
    return KnowConstructor.construct;
  }

  // Owned and regular objects (Color, Brush, Pen, Figure)

  return function(v) {
    if( v === null ) {
      // base.log(`${typeName} is null`);
      return v;
    }
    const Constructor = Type[typeName];
    const sameObject = v instanceof Constructor;
    if( Constructor.prototype.isOwnedObject ) {
      if( sameObject ) {
        // base.log(`${v.typeName}/${typeName} adopted by ${this.typeName}`);
        return v.adopted(this);
      }
      else {
        // base.log(`${v.typeName} used to construct ${typeName} adopted by ${this.typeName}`);
        return ( new Constructor(v) ).owner(this);
      }
    }
    else if( sameObject ) {
      // base.log(`${v.typeName}/${typeName} cloned`);
      return cloneDeep(v);
    }
    else {
      // base.log(`${v.typeName} used to construct ${typeName} for ${this.typeName}`);
      return new Constructor(v);
    }
  };
};

// [TODO] Review possible optimizations
const _makeReleaseFunction = function(owned) {
  return owned ? function(c) { if( c ) { c._owner=null; } return c; }
               : function(c) { return c; };
};

const _makeArrayConstructFunction = function(p) {
  const constructItem = p.constructItem;
  return function(c) {
    return map(c,constructItem,this);
  };
};

const _makeSetFunction = function(p) {
  if( !p.set && ( p.type === undefined || includes( builtIn, p.type ) ) ) {
    return null;
  }
  const set = p.set ;
  const construct = p.construct ;
  const m = p.member;
  return set ? function(c) { set.call(this,construct.call(this,c)); }
             : function(c) { this[m] = construct.call(this,c); };
};

function _addMember( prototype, callParent, name, member ) {

  // Check if we're overwriting an existing function
  if( isFunction(member) && isFunction(callParent[name]) && fnTest.test(member)) {
    prototype[name] = (function (name, fn) {
      return function() {
        const tmp = this.callParent;

        // Add a new .callParent() method that is the same method
        // but on the super-class
        this.callParent = callParent[name];

        // The method only need to be bound temporarily, so we
        // remove it when we're done executing
        const ret = fn.apply(this, arguments);
        this.callParent = tmp;

        return ret;
      };
    })(name, member);
  } else {
    prototype[name] = member;
  }
}

export const _extendPrototype = function( prototype, callParent, config ) {

  // Copy members over onto the new prototype

  forEach( keys(config), name => {
    const descriptor = Object.getOwnPropertyDescriptor(config,name);
    if( descriptor.get || descriptor.set ) {
      Object.defineProperty(prototype,name,descriptor);
    }
    else {
      _addMember(prototype,callParent,name,config[name]);
    }
  });

  // Handle properties

  const prop = 'Properties',
        inheritProps = callParent[prop],
        configProps = config[prop],
        thisProps = prototype[ prop ] = inheritProps ? clone( inheritProps ) : {};

  if( configProps ) {

    // Init member name
    forOwn( configProps, (p,name) => {

      if( thisProps[name] ) {
        error('Base error, Property redefiniton: '+name);
        return;
      }
      // getset function (named after the property id)
      if( prototype[name] !== undefined ) {
        error('Properties can not be overrided');
      }

      thisProps[name] = p;

      if( p.member === undefined && !p.redirect ) {
        p.member = '_' + name;
      }

      if( p.redirect ) {
        prototype[name] = p.redirect;
      }
      else {
        if( p.owned !== false ) {
          p.owned = true;
        }
        if( p.computed !== true ) {
          p.computed = false;
        }
        const isArray = p.type && typeof p.type === 'string' && p.type.indexOf('[]') !== -1;
        if( !isArray ) {
          if( ! p.construct ) {
            p.construct = _makeConstructFunction(p.type,p.owned);
          }
          const set = _makeSetFunction(p);
          definePropertyImp(prototype,name,p.member,p.get,set,p.onChange);
        }
        else {
          if( ! p.array ) {
            p.array = Base.Type.ArrayProperty;
          }
          if( ! p.constructItem ) {
            p.constructItem = _makeConstructFunction(p.type.substring(0,p.type.lastIndexOf('[')),p.owned);
          }
          if( ! p.releaseItem ) {
            p.releaseItem = _makeReleaseFunction(p.owned);
          }
          if( ! p.construct ) {
            p.construct = _makeArrayConstructFunction(p);
          }

          defineArrayPropertyImp(prototype,name,p.member);
        }
      }
    });
  }

  /*
  const defProps = config['defineProperties'];
  if( defProps ) {
    defineProperties(prototype,defProps);
  }*/

};

// The base Class implementation (does nothing)
const CxBase = function() {};

// Create a new Class that inherits from this class
CxBase.extend = function(...configs) {

  const callParent = this.prototype;

  // Instantiate a base class (but only create the instance, don't run the init constructor)
  initializing = true;
  const prototype = new this();

  initializing = false;

  let typeName;

  forEach( configs, config => {

    _extendPrototype( prototype, callParent, config );

    typeName = config.typeName || typeName;
  });

  // Use T as the varible name because it is shown in the dev tools

  // The dummy class constructor
  const T = function( ...params ) {

    if( ! initializing ) {

      const { constructor } = prototype;
      if( ! ( this instanceof constructor ) ) {
        return new constructor(...params);
      }

      const debugProps = Config.debugProps && prototype.__debugProps;
      let _this = this;
      if( debugProps ) {
        _this = createSafeProxy(this);
      }

      // Possible optimization, avoid changing object structure later
      _this.callParent = noop;

      _this._events = new Map();

      // We first init the properties so they can be accesed from the init function
      _this.initProperties(...params);

      // All construction is actually done in the init method
      _this.init(...params);

      // After initialization is done
      _this.afterInit(...params);

      // Start checks
      if( debugProps ) {
        _this.__debugPropsReady = true;
        return _this;
      }
    }
  };

  // Populate our constructed prototype object
  T.prototype = prototype;

  // Enforce the constructor to be what we expect
  T.prototype.constructor = T;

  // And make this class extendable
  T.extend = this.extend;

  if( typeName ) {
    Type[typeName] = T;
  }

  return T;
};


export const Base = CxBase.extend({

  // object.on( 'event', fn, scope )
  onEvent( event, handler, scope ) {

    const eventHandlers = this._events.get(event);

    if( ! eventHandlers ) {
      error(`${this.typeName}.on(${event},...), event doesn't exist`);
    }
    if( ! isFunction(handler) ){
      error(`${this.typeName}.on( ${event}, ... handler `+ ( handler ? 'is not a function' : 'undefined' ) );
    }

    eventHandlers.push({ handler, scope });
    return this;
  },

  // object.on({ 'event1': fn1, 'event2': fn2 }, scope });
  onEvents( events, scope ) {
    forOwn( events, (handler,event) => {
      if( ! handler ) {
        error(`${this.typeName}.on( { ${event}: ... handler undefined`);
      }
      if( ! isFunction(handler) ) {
        error(`${this.typeName}.on( { ${event}: { fn: fn, scope: scope } ) is unsupported, use .on( { ${event}: fn }, scope ) instead`);
      }
      else {
        this.on(event,handler,scope);
      }
    });
    return this;
  },

  on( event, handler, scope ) {
    // object.on({ 'event1': fn1, 'event2': fn2 }, scope });
    if( typeof event !== 'string' ) {
      return this.onEvents( event, handler );
    }
    return this.onEvent( event, handler, scope );
  },

  // object.un( event, handler, scope );
  unEvent( event, handler, scope ) {
    const eventHandlers = this._events.get(event);

    if( ! eventHandlers ){
      error(`${this.typeName}.un( ${event}, ... ), event doesn't exist`);
    }
    if( ! isFunction(handler) ){
      error(`${this.typeName}.un( ${event}, ... ), handler ` + ( handler ? 'is not a function' : 'undefined' ) );
    }

    // Loop down, just in case handler was added multiple times (and will be removed multiple times)
    for( let i = eventHandlers.length - 1; i >= 0; i-- ) {
      const eventHandler = eventHandlers[i];
      if( eventHandler.handler === handler && ( ! scope || eventHandler.scope === scope)) {
        eventHandlers.splice(i, 1);
      }
    }
  },

  // object.un( { 'event1': handler1, 'event2': handler2 }, scope )
  unEvents( config, scope ) {
    forOwn( config, (handler,event) => {
      if( ! isFunction(handler) ) {
        error(`${this.typeName}.un( { ${event}: { fn: fn, scope: scope } } ) is unsupported, use .un( { ${event}: fn }, scope ) instead`);
      }
      this.un(event,handler,scope);
    });
    return this;
  },

  // Remove every function that has this scope
  unScope( scope ) {
    this._events.forEach( eventHandlers => {
      // Loop down, just in case handler was added multiple times (and will be removed multiple times)
      for( let i = eventHandlers.length-1; i >= 0; --i ) {
        if( eventHandlers[i].scope === scope ) {
          eventHandlers.splice(i,1);
        }
      }
    });
    return this;
  },

  un( event, handler, scope ) {
    if( typeof event !== 'string' ) {
      if( isPlainObject(event) ) {
        return this.unEvents(event,handler);
      }
      else {
        return this.unScope(event);
      }
    }
    return this.unEvent(event,handler,scope);
  },

  // this.addEvents('renderstart', rendercomplete');
  addEvents() {
    forEach( arguments, param => {
      if( isArray(param) ){
        this.addEvents(...param);
      }
      else {
        this._events.set(param,[]);
      }
    });
    return this;
  },

  hasEvent( event ) {
    return this._events.has(event);
  },

  enumEvents() {
    return this._events.keys();
  },

  fireEvent( event, ...params ) {

    const handlers = this._events.get(event);
    if( ! handlers ) {
      error(`${this.typeName}.fireEvent( ${event}, ... ), event doesn't exist`);
    }

    // Prevent error caused by calls to remove a listener from the handler
    const c_handlers = clone(handlers);

    for( let i = 0, iEnd = c_handlers.length; i < iEnd; ++i ) {
      const { handler, scope } = c_handlers[i];
      handler.apply( scope, params );
    }
  },

  // Properties
  copyProperties( c ) {
    const properties = this.Properties;
    const names = keys(properties);
    for( let k = 0, kEnd = names.length; k < kEnd; ++k ) {
      const p = properties[names[k]];
      if( ! p.redirect ) {

        const m = p.member;
        if( !p.array ) {
          if( c && c[m] !== undefined ) {
            let v = c[m];
            // [TODO] Currently Matrix, Point, etc are not owned objects
            // This is a hack to clone them when copying properties. This code needs to get cleaned
            // up to properly handle this cases. This is a hack until that moment:
            if( v && v._owner === undefined && v.clone ) { v = v.clone(); }
            this[m] = p.construct.call(this, v);
          }
          else if( p.def !== undefined ) {
            let v = (p.def && p.def.clone) ? p.def.clone() : p.def;
            this[m] = p.construct.call(this, v );
          }
          else { this[m] = null; }
        }
        else {
          if( c && c[m] !== undefined ) {
            this[m] = new p.array( c[m], this, p );
          }
          else {
            const array = new p.array( p.def ? cloneDeep(p.def):[], this, p );
            this[m] = array;
          }
        }
      }
    }
  },

  initProperties( c ) {
    const properties = this.Properties;
    if( c && c.Properties === properties ) {
      // init from same object ( new Obj(this) clonning idiom )
      this.copyProperties(c);
    }
    else {
      // Init from plain object { p1: ..., p2: ... }
      // Direct JSON represetnation of objets is allowed
      const names = keys(properties);
      for( let k = 0, kEnd = names.length; k < kEnd; ++k ) {
        const name = names[k];
        const p = properties[name];
        if( ! p.redirect ) {

          const m = p.member;
          if( !p.array ) {
            if( c && c[name] !== undefined ) {
              let v = c[name];
              this[m] = ( v !== null && p.construct ) ? p.construct.call(this,v) : v;
            }
            else if( p.def !== undefined ) {
              let v = (p.def && p.def.clone) ? p.def.clone() : p.def;
              this[m] = ( v !== null && p.construct ) ? p.construct.call(this,v) : v;
            }
            else { this[m] = null; }
          }
          else {
            const a = ( c && c[name] !== undefined ) ? c[name]
                                           : p.def ? cloneDeep(p.def) : [];
            this[m] = new p.array( a, this, p );
          }
        }
      }
    }
  },

  init() {

  },

  afterInit() {

  },

  setupProperties( c ) {
    const properties = this.Properties;
    // Setup from plain object { p1: ..., p2: ... }
    // Direct JSON representation of objets is allowed
    const names = keys(properties);
    for( let k = 0, kEnd = names.length; k < kEnd; ++k ) {
      const name = names[k];
      const p = properties[name];
      if( ! p.redirect ) {

        if( !p.array ) {
          if( c && c[name] !== undefined ) {
            this[name] = c[name]; // this[name]( c[name] );
          }
          else if( p.def !== undefined ) {
            const v = (p.def && p.def.clone) ? p.def.clone() : p.def;
            this[name] = v;// this[name]( v ) ;
          }
          else {
            this[p.member] = null;
          }
        }
        else {
          const a = ( c && c[name] !== undefined ) ? c[name]
                                                 : p.def ? cloneDeep(p.def) : [];
          if( this[p.member] ) {
            this[p.member].set(a);
          }
          else {
            this[p.member] = new p.array(a,this,p);
          }
        }
      }
    }
  },

  propertiesAreEqual( other ) {
    const properties = this.Properties;
    if( ! other || other.Properties !== properties ) {
      return false;
    }
    const names = keys(properties);
    for( let k = 0, kEnd = names.length; k < kEnd; k++ ) {
      const p = properties[names[k]];
      if( ! p.computed ) {
        const m = p.member;
        const v = this[m];
        if( v && v.isEqual ) {
          if( ! v.isEqual(other[m]) ) {
            return false;
          }
        }
        else if( v !== other[m] ) {
          return false;
        }
      }
    }
    return true;
  },

  // Default clone for properties only classes
  clone() {
    return new this.constructor(this);
  },
  isEqual(other) {
    return this.propertiesAreEqual(other);
  },

  _bind( s ) {
    if( ! this[s] ){
      console.log(`_bind: ${this.typeName}.${s} is undefined`);
    }
    this[s] = this[s].bind(this);
    return this;
  }
});

Base.Type = Type;


// Work in progress
// This will be relocated
// [TODO] add onOwnerChange

export const OwnedObject = Base.extend({ typeName: 'OwnedObject',

  _owner: null,

  get isOwnedObject() {
    return true;
  },

  owner( v ) {
    return v===undefined?this._owner:(this._owner=v,this);
  },

  adopted( owner ) {
    const a = ( this._owner === null ) ? this : cloneDeep(this);
    a.owner(owner);
    return a;
  },

  init( /*c*/ ) {
    // Enable? if( c && c.owner ) this._owner = c.owner;
  },
  
  invalidate() {
    const owner = this._owner;
    if( owner && owner.invalidate ) {
      owner.invalidate();
    }
  },
  invalidateView() {
    const owner = this._owner;
    if( owner && owner.invalidateView ) {
      owner.invalidateView();
    }
  }

});


OwnedObject.adoptObject = function(object, owner) {
  const adoptedObject = ( object._owner === null ) ? object : cloneDeep( object );
  adoptedObject.owner(owner);
  return adoptedObject;
};

OwnedObject.adoptArray = function(array, owner) {
  return map( array, object => OwnedObject.adoptObject(object,owner) );
};

OwnedObject.adopt = function(thing, owner) {
  return thing.length !== undefined ? OwnedObject.adoptArray(thing,owner) : OwnedObject.adoptObject(thing,owner);
};

OwnedObject.Type = Type;

// [TODO]
export const invalidateOwner = function() {
  const owner = this._owner;
  if( owner && owner.invalidate ) {
    owner.invalidate();
  }
};

export function forwardPropertiesFrom(Type,name,proto) {
  forOwn( Type.prototype.Properties, (definition,prop) => {
    if( ! Object.hasOwnProperty(proto,prop) ) {
      Object.defineProperty(proto,prop,{
        get() {
          return this[name][prop];
        },
        set(value) {
          this[name][prop] = value;
        },
        enumerable: true
      });
    }
  });
  return proto;
}

// Proxy traps to check properties
// Use Config.debugProps = true to enable

const propsWhitelist = {};
forEach([
  'length', 'then',
  '__CxArray', 'clone', 'cloneDeep', '_ignoreAttributes',
  'decode',
  'isText','isShape','isRaster','isGroup','isProxyGroup',
  'isDocument','isPage','isLayer','is3D'
], p => propsWhitelist[p] = true );

function checkAccess(target,property) {
  return ! isSymbol(property) &&
         ! ( property in propsWhitelist ) &&
         ! ( property in target );
}


function checkPropType(type,value) {
  switch( type ) {
    case 'string':
    case 'magnitude':
      if( typeof value !== 'string' ) {
        return true;
      }
      break;
    case 'number':
    case 'angle':
    case 'integer':
      if( typeof value !== 'number' ) {
        return true;
      }
      break;
    case 'boolean':
      if( typeof value !== 'boolean' ) {
        return true;
      }
      break;
  }
  return false;
}

function getPropertyDescriptor(obj, key) {
  return obj && ( Object.getOwnPropertyDescriptor(obj, key) || getPropertyDescriptor(Object.getPrototypeOf(obj), key) );
}

const safeProxy = {

  get( target, property, receiver ) {
    if( target.__debugPropsReady && checkAccess(target,property) ) {
      const error = 'get error: ' + target.typeName + '.' + property;
      console.log(error);
      target[property](); // better than throw, shows in console even if catched in promises chains
    }
    const descriptor = getPropertyDescriptor(receiver,property);
    if( descriptor && descriptor.get ) {
      return descriptor.get.call(receiver);
    }
    else {
      return target[property];
    }
  },

  set( target, property, value, receiver ) {
    if( target.__debugPropsReady && checkAccess(target,property) ) {
      const error = 'set error: ' + target.typeName + '.' + property;
      console.log(error);
      target[property](); // better than throw, shows in console
    }
    const Properties = target.Properties;
    if( value != null && Properties ) {
      const p = Properties[property];
      if( p ) {
        var error = checkPropType(p.type,value);
        if( error ) {
          const error = 'set error: ' + target.typeName + '.' + property + " = " + value + ' (wrong type, should be: ' + p.type + ')';
          console.log(error);
          throw(error);
        }
      }
    }
    const descriptor = getPropertyDescriptor(receiver,property);
    if( descriptor && descriptor.set ) {
      descriptor.set.call(receiver,value);
    }
    else {
      target[property] = value;
    }
    return true;
  }
};

function createSafeProxy( object ) {
  // We need to wrap the object in the Proxy before initialization
  // If not, the _owner of properties will point to the target object
  // console.log(object.typeName);
  return new Proxy(object,safeProxy);
}



/** WEBPACK FOOTER **
 ** ./src/gear/base.js
 **/